import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiX, FiAlertCircle, FiClock, FiArrowRight } from "react-icons/fi";
import { useGetProgramsNeedingFeedbackQuery } from "../../utils/api/endpoints/business/businessFeedbackApi";
import { motion, AnimatePresence } from "framer-motion";

const FeedbackReminderModal = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetProgramsNeedingFeedbackQuery();
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Debug: Log component mount
  useEffect(() => {
    console.log("FeedbackReminderModal - Component mounted");
    // Removed sessionStorage check to allow showing on every reload/login
  }, []);

  useEffect(() => {
    console.log("FeedbackReminderModal - useEffect triggered");
    console.log("FeedbackReminderModal - Current state:", {
      isLoading,
      hasData: !!data,
      programsCount: data?.programs?.length || 0,
      dismissed,
      isVisible
    });

    // Don't do anything while loading
    if (isLoading) {
      console.log("FeedbackReminderModal - Still loading, skipping");
      return;
    }

    // Don't do anything if already dismissed
    if (dismissed) {
      console.log("FeedbackReminderModal - Already dismissed, skipping");
      return;
    }

    // Don't do anything if no data
    if (!data || !data.programs || data.programs.length === 0) {
      console.log("FeedbackReminderModal - No data or no programs, skipping");
      return;
    }

    // Removed sessionStorage check

    // All conditions met - show the modal
    console.log("FeedbackReminderModal - ✅ All conditions met! Will show modal in 2 seconds");
    console.log("FeedbackReminderModal - Programs count:", data.programs.length);

    const timer = setTimeout(() => {
      console.log("FeedbackReminderModal - ⏰ Timer fired! Setting isVisible to true");
      setIsVisible(true);
    }, 2000);

    return () => {
      console.log("FeedbackReminderModal - Cleaning up timer");
      clearTimeout(timer);
    };
  }, [data, isLoading, error, dismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissed(true);
    // Removed sessionStorage setItem
  };

  const handleNavigateToFeedback = (programId: number, runId?: number) => {
    handleDismiss();
    navigate(`/dashboard/programs/${programId}/feedback${runId ? `?runId=${runId}` : ""}`);
  };

  // Debug: Always show component status in console
  useEffect(() => {
    console.log("FeedbackReminderModal - Render check:", {
      isLoading,
      hasData: !!data,
      programsCount: data?.programs?.length || 0,
      dismissed,
      isVisible,
      willReturn: isLoading || !data || (data && data.programs.length === 0) || dismissed
    });
  });

  // Early returns for non-rendering cases
  if (isLoading) {
    console.log("FeedbackReminderModal - Returning null: isLoading");
    return null;
  }

  if (!data) {
    console.log("FeedbackReminderModal - Returning null: no data");
    return null;
  }

  if (data.programs.length === 0) {
    console.log("FeedbackReminderModal - Returning null: no programs");
    return null;
  }

  if (dismissed) {
    console.log("FeedbackReminderModal - Returning null: dismissed =", dismissed);
    return null;
  }

  console.log("FeedbackReminderModal - Rendering modal container, isVisible:", isVisible);
  console.log("FeedbackReminderModal - Data available:", data);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 20, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 20, y: -20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed top-24 right-6 z-[9999] w-80"
        >
          <div className="relative group overflow-hidden bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-gray-100 p-4">
            {/* Left Accent Bar */}
            <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600" />

            {/* Header */}
            <div className="flex items-center justify-between mb-4 pl-1">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 shadow-sm">
                  <FiAlertCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 leading-tight">
                    Pending Feedback
                  </h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                      {data.programs.length} Action{data.programs.length !== 1 ? "s" : ""} Required
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Items List */}
            <div className="space-y-1 pl-1 max-h-[360px] overflow-y-auto pr-1 custom-scrollbar">
              {data.programs.slice(0, 5).map((program) => (
                <div
                  key={`${program.program_id}-${program.run_id}`}
                  onClick={() => handleNavigateToFeedback(program.program_id, program.run_id)}
                  className="group/item flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-all border border-transparent hover:border-slate-100"
                >
                  <div className="flex-1 min-w-0 pr-3">
                    <h4 className="text-[13px] font-bold text-gray-800 truncate leading-snug group-hover/item:text-blue-600 transition-colors">
                      {program.program_title}
                    </h4>
                    {program.run_title && (
                      <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">
                        {program.run_title}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                      <FiClock className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] text-slate-500 font-semibold">
                        {program.days_since_completion === 0 ? "Today" : `${program.days_since_completion}d ago`}
                      </span>
                    </div>
                  </div>
                  <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center group-hover/item:bg-blue-600 transition-all">
                    <FiArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover/item:text-white" />
                  </div>
                </div>
              ))}
              
              {data.programs.length > 5 && (
                <div className="pt-2 pb-1 text-center">
                  <span className="px-3 py-1 rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                    + {data.programs.length - 5} more items
                  </span>
                </div>
              )}
            </div>

            {/* Subtle Footer */}
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between pl-1">
              <span className="text-[10px] text-slate-400 font-medium italic">
                Your feedback helps us improve
              </span>
              <button 
                onClick={handleDismiss}
                className="text-[11px] font-bold text-slate-500 hover:text-slate-800 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackReminderModal;
